/** @jsxImportSource @emotion/react */

import React, { useEffect, useState } from 'react';
import { getAttendanceType } from '@util/attendance';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import {
  EApiAttendanceType,
  EApiCharacterProblemType,
  EApiCharacterType,
  EApiLanguageCode,
  EApiStudyType,
  EApiSubCharacterType,
  IClassroomCharacterGroupData,
  IClassroomCharacterGroupWorkbookData,
} from '@datatype/iyagigo/api-type';
import { ICharacterQuizQuestion } from '@datatype/iyagigo/type';
import { createGroupQuiz } from '@component/iyagigo/classroom/character-study/character-quiz';
import CharacterCard from '@component/iyagigo/classroom/character-study/character-card';
import { useModifyCharacterGroupWorkbookMutation, useCharacterGroupWorkbooksQuery } from '@hook/iyagigo/useApi';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { usePlaySound } from '@hook/common/useSound';
import { useCheckAttendance, useRecordStudy } from '@hook/iyagigo/useStudent';
import { useActiveTime } from '@hook/common/useEvent';
import { actions as modalActions } from '@store/slice/modal-slice';
import TextBlock from '@component/common/block/text-block';
import ImageAudioButton from '@design-system/image-audio-button';
import TextButton from '@design-system/text-button';
import Spinner from '@design-system/spinner';
import BottomContainer from '@component/common/architecture/bottom-container';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import Gap from '@component/common/gap';


// ###################################
// CharacterStudy — 글자 학습 (카드 → 퀴즈 → 완료)
// - 라우트: /ig/classroom/character (URL: ?characterType=hiragana&group=a)
// - 글자 목록(행 선택)은 character-home(CharacterHome)에서 분리. 행 클릭 시 이 라우트로 진입.
// - 시각 요소는 design-system / common block 활용, styled는 레이아웃 컨테이너에만
// ###################################

// 레이아웃 패턴 — Wrapper(height:100%) + CardContentContainer(flex:1) + BottomContainer 하단 고정. phase 조건 렌더.
const BOTTOM_CONTAINER_HEIGHT = '55px';
// const BOTTOM_CONTAINER_HEIGHT = '85px';

// ############### styled (레이아웃 전용) ###############
const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0 ${size.PAGE_PADDING};
  display: flex;
  flex-flow: column nowrap;
`;

// 콘텐츠 영역(flex:1) — card는 카드 자체가 화면을 채우고(내부 스크롤), quiz는 문제+선택지. 공용 Scroll 불필요.
const CardContentContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-flow: column nowrap;
`;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: ${size.MARGIN_1};
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

// 채점 후 상단 컨트롤 줄 (다음문제 버튼 우측 정렬) — cloze 패턴
const ControlRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  width: 100%;
`;

const NAVIGATION_BUTTON_WIDTH = '80px';

const QuizContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

// 문제 카드 — cloze 문제 카드 톤(연한 브라운 배경 + 그림자 없음). 그림자 없어 겹침·잘림 문제 없음.
const QuestionCard = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  height: 160px;
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.BROWN_4};
`;

const ChoiceList = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  width: 100%;
`;

// cloze VocabularyChoice 패턴 — div + 상태별 테두리 색만 변경 (배경 흰색 고정)
const ChoiceContainer = styled.div<{ $borderColor: string; $answered: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 5px;
  border-radius: ${size.BORDER_RADIUS_1};
  cursor: ${({ $answered }) => ($answered ? 'default' : 'pointer')};
  background-color: ${color.WHITE_1};
  border: 1px solid ${({ $borderColor }) => $borderColor};
`;

// label(중앙 글자) — 위치 고정, sublabel은 이 기준으로 오른쪽에 붙임
const LabelContainer = styled.div`
  position: relative;
  display: flex;
`;

// 채점 후 병기 발음 — label을 밀지 않도록 글자 바로 오른쪽에 absolute로 붙임
// width: max-content — absolute 요소는 left가 부모 너비를 넘으면 가용 너비 0으로 수축해
// 글자 단위 세로 줄바꿈이 생기므로, 콘텐츠 너비만큼 강제 확보 (단어 sublabel 대응)
const SublabelContainer = styled.div`
  position: absolute;
  left: calc(100% + ${size.MARGIN_1});
  top: 50%;
  transform: translateY(-50%);
  width: max-content;
  white-space: nowrap;
`;

// 로딩 Spinner 중앙 배치용
const CompleteWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 40px 24px;
  gap: 20px;
`;

// 선택지 테두리 색 (cloze createBorderDesign 패턴) — 채점 전: 선택만 노랑, 채점 후: 정답 초록/오답 빨강
const getChoiceBorderColor = (isAnswerView: boolean, isSelected: boolean, isCorrect: boolean): string => {
  if (isAnswerView) {
    if (isCorrect) return color.GREEN_4;
    if (isSelected) return color.RED_3;
  } else if (isSelected) {
    return color.YELLOW_3;
  }
  return color.GRAY_4;
};

// ###################################
// CharacterStudy — 카드 → 퀴즈 → 완료 (/ig/classroom/character)
// URL: ?characterType=hiragana&group=a
// ###################################
type TStudyPhase = 'card' | 'quiz';

interface IProps {
  // 헤더 프로그레스바 갱신용 — 통합 진행(카드+퀴즈)을 페이지로 올림. total=0이면 헤더 숨김
  onProgress?: (progress: { current: number; total: number }) => void;
}

const CharacterStudy: React.FC<IProps> = ({ onProgress }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useTypedDispatch();
  const playSound = usePlaySound();
  const checkAttendance = useCheckAttendance();
  const recordStudy = useRecordStudy();
  const { getActiveTime, reset: resetActiveTime } = useActiveTime(true);
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage);
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';

  // ############### URL 파라미터 (학습 진입 컨텍스트) ###############
  const searchParams = new URLSearchParams(location.search);
  const characterTypeParam = searchParams.get('characterType');
  const groupParam = searchParams.get('group');
  const characterType =
    characterTypeParam === EApiCharacterType.KATAKANA ? EApiCharacterType.KATAKANA : EApiCharacterType.HIRAGANA;

  // ############### state ###############
  const [phase, setPhase] = useState<TStudyPhase>('card');
  const [cardIndex, setCardIndex] = useState<number>(0);
  const [quizQuestions, setQuizQuestions] = useState<ICharacterQuizQuestion[]>([]);
  const [quizIndex, setQuizIndex] = useState<number>(0);
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);
  const [isAnswerView, setIsAnswerView] = useState<boolean>(false); // 채점 여부 (cloze 패턴)
  const [correctCount, setCorrectCount] = useState<number>(0);
  const [incorrectCharacters, setIncorrectCharacters] = useState<string[]>([]);

  // ############### hook ###############
  const { data: characterGroupWorkbooksResponse, isLoading } = useCharacterGroupWorkbooksQuery(
    EApiLanguageCode.JAPANESE,
    characterType,
  );
  const workbookMutation = useModifyCharacterGroupWorkbookMutation(EApiLanguageCode.JAPANESE, characterType);

  // ############### variable ###############
  const characterGroupWorkbooks: IClassroomCharacterGroupWorkbookData[] = characterGroupWorkbooksResponse?.data ?? [];
  const characterGroups: IClassroomCharacterGroupData[] = characterGroupWorkbooks.map(
    (workbook) => workbook.characterGroup,
  );
  const groupKeys = characterGroups.map((group) => group.groupKey);
  const currentGroupMetadata = characterGroups.find((group) => group.groupKey === groupParam);
  const currentWorkbook = characterGroupWorkbooks.find((workbook) => workbook.characterGroup.groupKey === groupParam);
  const currentGroupCharacters = [...(currentGroupMetadata?.characters ?? [])].sort(
    (a, b) => a.characterOrder - b.characterOrder,
  );
  const hasGroup = groupKeys.includes(groupParam ?? '') && currentGroupCharacters.length > 0;
  const currentCard = currentGroupCharacters[cardIndex] ?? null;
  const currentQuestion = quizQuestions[quizIndex] ?? null;

  // 퀴즈 문제 수 — 문제는 글자별 problem(백엔드 큐레이션)에서 옴 (일반 글자 읽1+듣1, 촉음/요음 읽2+듣2)
  const quizQuestionCount = currentGroupCharacters.reduce((count, c) => count + (c.problem?.length ?? 0), 0);

  // 행 라벨 — 일반 행은 'あ행', 작게 쓰는 글자 행은 촉음/요음 (완료 모달 표기)
  const firstCharacter = currentGroupCharacters[0];
  const groupLabel =
    currentGroupMetadata?.subCharacterType === EApiSubCharacterType.SOKUON
      ? '촉음'
      : currentGroupMetadata?.subCharacterType === EApiSubCharacterType.YOON
        ? '요음'
        : `${firstCharacter?.symbol ?? ''}행`;

  // 통합 진행(카드+퀴즈) — 총 스텝 = 카드 수 + 퀴즈 문제 수
  const cardCount = currentGroupCharacters.length;
  const totalSteps = cardCount + quizQuestionCount;
  const currentStep = phase === 'quiz' ? cardCount + quizIndex : cardIndex;

  // 그룹 정보가 없으면(직접 URL 진입 등) 목록으로 복귀 (로딩 끝난 뒤 판단)
  useEffect(() => {
    if (!isLoading && !hasGroup) navigate(IYAGIGO_PATH.CLASSROOM_HOME_PATH, { replace: true });
  }, [isLoading, hasGroup, navigate]);

  // 진행 상황을 페이지(헤더 프로그레스바)로 올림
  useEffect(() => {
    onProgress?.({ current: currentStep, total: totalSteps });
  }, [onProgress, currentStep, totalSteps]);

  // ############### handler ###############
  const handleNextCard = () => {
    recordStudy({
      studentKey,
      studyType: EApiStudyType.CHARACTER,
      languageCode: studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
      studyTime: getActiveTime(),
      studyCount: 1,
    });
    resetActiveTime();

    if (cardIndex < currentGroupCharacters.length - 1) {
      setCardIndex(cardIndex + 1);
      return;
    }
    // 카드 끝 → 그룹 퀴즈 시작 (글자별 problem을 화면용 문제로 변환 — 순서는 데이터가 결정)
    setQuizQuestions(createGroupQuiz(currentGroupCharacters));
    setQuizIndex(0);
    setSelectedChoiceId(null);
    setIsAnswerView(false);
    setCorrectCount(0);
    setPhase('quiz');
  };

  // 채점 전에는 선택만 저장 (재선택 허용), 채점 후에는 잠금
  const handleSelectChoice = (choiceId: string) => {
    if (!currentQuestion || isAnswerView) return;
    setSelectedChoiceId(choiceId);
  };

  // 채점하기 — 정오답 공개 + 정답 카운트. 마지막 문제면 채점 즉시 완료 모달 (cloze 패턴)
  const handleScoring = () => {
    if (!currentQuestion || selectedChoiceId === null || isAnswerView) return;
    recordStudy({
      studentKey,
      studyType: EApiStudyType.CHARACTER,
      languageCode: studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
      studyTime: getActiveTime(),
    });
    resetActiveTime();
    setIsAnswerView(true);

    const choice = currentQuestion.choices.find((item) => item.id === selectedChoiceId);
    const isCorrect = choice?.isCorrect ?? false;
    const incorrectCharacter = currentQuestion.character;
    playSound(isCorrect ? 'correct' : 'incorrect');
    if (isCorrect) setCorrectCount((prev) => prev + 1);
    else if (incorrectCharacter) setIncorrectCharacters((prev) => [...prev, incorrectCharacter]);

    // 마지막 문제 채점 → 완료 기록 후 축하 모달
    const isLastQuestion = quizIndex === quizQuestions.length - 1;
    if (isLastQuestion) {
      const finalCount = correctCount + (isCorrect ? 1 : 0);
      const finalIncorrectCharacters =
        !isCorrect && incorrectCharacter ? [...incorrectCharacters, incorrectCharacter] : incorrectCharacters;
      const incorrectCount = quizQuestions.length - finalCount;
      checkAttendance(getAttendanceType(studyLanguage, 'character', 'classroom'));
      if (currentWorkbook?.key) {
        workbookMutation.mutate({ workbookKey: currentWorkbook.key, isStudy: true });
      }
      dispatch(
        modalActions.showContentModal({
          type: 'study-celebration',
          data: {
            title: '오늘도 해냈어요!',
            description: `<strong>${groupLabel}</strong> 학습을 끝까지 마쳤어요`,
          },
        }),
      );
    }
  };

  const handleNextQuestion = () => {
    // 마지막 문제 → 끝내기(홈 복귀). 완료 모달은 채점 시점에 이미 표시됨
    if (quizIndex >= quizQuestions.length - 1) {
      navigate(IYAGIGO_PATH.CLASSROOM_HOME_PATH);
      return;
    }
    setQuizIndex(quizIndex + 1);
    setSelectedChoiceId(null);
    setIsAnswerView(false);
  };

  // 로딩 중 Spinner
  if (isLoading) {
    return (
      <Wrapper>
        <CardContentContainer>
          <CompleteWrapper>
            <Spinner size='32px' color={color.BROWN_1} />
          </CompleteWrapper>
        </CardContentContainer>
      </Wrapper>
    );
  }

  if (!hasGroup) return null;

  // ############### render 파생값 ###############
  const isLastCard = cardIndex === currentGroupCharacters.length - 1;
  const hasSelection = selectedChoiceId !== null;
  const isCorrect = currentQuestion?.choices.find((item) => item.id === selectedChoiceId)?.isCorrect ?? false;
  const isListening = currentQuestion?.type === EApiCharacterProblemType.LISTENING;
  const questionGlyph = currentQuestion?.question ?? '';
  const isLastQuestion = quizIndex === quizQuestions.length - 1;

  const Choices = (currentQuestion?.choices ?? []).map((choice) => {
    const isSelected = selectedChoiceId === choice.id;
    return (
      <ChoiceContainer
        key={choice.id}
        $borderColor={getChoiceBorderColor(isAnswerView, isSelected, choice.isCorrect)}
        $answered={isAnswerView}
        onClick={() => handleSelectChoice(choice.id)}>
        <LabelContainer>
          <TextBlock
            text={choice.label}
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: {} }}
          />
          {isAnswerView && choice.sublabel && (
            <SublabelContainer>
              <TextBlock
                text={choice.sublabel}
                design={{ text: { size: 'small', color: color.GRAY_1, bold: false }, box: {} }}
              />
            </SublabelContainer>
          )}
        </LabelContainer>
      </ChoiceContainer>
    );
  });

  // ############### render ###############
  // grammar-study 패턴: 단일 return + phase별 조건 렌더 (early return 지양)
  return (
    <Wrapper>
      <CardContentContainer>
        {phase === 'card' && currentCard && (
          <CharacterCard character={currentCard} subCharacterType={currentGroupMetadata?.subCharacterType} />
        )}
        {phase === 'quiz' && currentQuestion && (
          <QuizContainer>
            <QuestionCard>
              {isListening ? (
                <>
                  <TextBlock
                    text='발음을 듣고 알맞은 표기를 고르세요'
                    design={{ text: { size: 'medium', color: color.GRAY_1 }, box: {} }}
                  />
                  <Gap height={size.MARGIN_2} />
                  {currentQuestion.audioLink && (
                    <ImageAudioButton
                      sources={[currentQuestion.audioLink]}
                      theme={ETheme.BROWN}
                      tone='normal'
                      kind='outlined'
                      size='medium'
                      shape='round'
                      width='45px'
                    />
                  )}
                </>
              ) : (
                <>
                  <TextBlock
                    text='알맞은 발음을 고르세요'
                    design={{ text: { size: 'medium', color: color.GRAY_1 }, box: {} }}
                  />
                  <Gap height={size.MARGIN_2} />
                  <TextBlock
                    text={questionGlyph}
                    design={{ text: { size: 'huge', color: color.BLACK_2, bold: true }, box: {} }}
                  />
                </>
              )}
            </QuestionCard>
            <Gap height={size.MARGIN_2} />
            <ChoiceList>{Choices}</ChoiceList>
          </QuizContainer>
        )}
      </CardContentContainer>
      <BottomContainer
        position='relative'
        height={phase === 'card' ? BOTTOM_CONTAINER_HEIGHT : 'auto'}
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}
        animationKey={phase === 'quiz' ? (isAnswerView ? 'answer' : 'question') : undefined}>
        <BottomContentContainer>
          {phase === 'card' && (
            <TextButton
              text={isLastCard ? '연습하기' : '다음'}
              theme={ETheme.BROWN}
              shape='pill'
              size='medium'
              width='100%'
              handleClick={handleNextCard}
            />
          )}
          {phase === 'quiz' && (
            <>
              {isAnswerView && (
                <ControlRowContainer>
                  <TextButton
                    theme={ETheme.BROWN}
                    size='medium'
                    shape='square'
                    width={NAVIGATION_BUTTON_WIDTH}
                    text={isLastQuestion ? '끝내기' : '다음문제'}
                    handleClick={handleNextQuestion}
                  />
                </ControlRowContainer>
              )}
              {isAnswerView ? (
                <TextButton
                  theme={isCorrect ? ETheme.GREEN : ETheme.RED}
                  size='medium'
                  shape='pill'
                  width='100%'
                  text={isCorrect ? '정답이에요' : '오답이에요'}
                  icon={isCorrect ? 'circle' : 'close'}
                />
              ) : (
                <TextButton
                  theme={ETheme.BROWN}
                  size='medium'
                  shape='pill'
                  width='100%'
                  text='채점하기'
                  disabled={!hasSelection}
                  handleClick={handleScoring}
                />
              )}
            </>
          )}
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default CharacterStudy;
